import React, { useEffect, useRef } from "react";
import SearchInput from "../FormElements/SearchInput";
import IconifyIcon from "../IconifyIcon";
import { Button, CircularProgress, Divider } from "@mui/material";
import { useAppSelector } from "../../app/hooks";
import { selectLabels } from "../../slices/language";
import dayjs from "dayjs";
import { AccessData } from "../../api/models/access-model";
import { toAbsoluteMediaUrl } from "../../utils/getAccessTransactionPhotoUrl";

interface ManualExitLogProps {
  visitorSearch: string;
  setVisitorSearch: React.Dispatch<React.SetStateAction<string>>;
  selectedAccessToExit: AccessData | null;
  setSelectedAccessToExit: React.Dispatch<
    React.SetStateAction<AccessData | null>
  >;
  photos: { type: string; url: string }[];
  selectedPhoto: {
    type: string;
    url: string;
  } | null;
  setSelectedPhoto: React.Dispatch<
    React.SetStateAction<{
      type: string;
      url: string;
    } | null>
  >;
  setShowPictureModal: React.Dispatch<React.SetStateAction<boolean>>;
  accessesToExit: AccessData[] | null;
  accessesLoading: boolean;
  onLogExit: () => Promise<void>;
}

const ManualExitLog = ({
  visitorSearch,
  setVisitorSearch,
  selectedAccessToExit,
  setSelectedAccessToExit,
  photos,
  selectedPhoto,
  setSelectedPhoto,
  setShowPictureModal,
  accessesToExit,
  accessesLoading,
  onLogExit,
}: ManualExitLogProps) => {
  const labels = useAppSelector(selectLabels);
  const selectedContentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (selectedAccessToExit && selectedContentRef.current) {
      selectedContentRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [selectedAccessToExit]);

  const imageToShow = React.useMemo(
    () => toAbsoluteMediaUrl(selectedPhoto?.url),
    [selectedPhoto],
  );

  return (
    <div
      className="w-100 border-box hidden flex flex-dir-col align-items-center gap-6 p-8"
      style={{
        backgroundColor: "white",
        border: "1px #e0e0e0 solid",
        borderRadius: 6,
      }}
    >
      <SearchInput
        value={visitorSearch}
        setValue={setVisitorSearch}
        size={"small"}
      />
      {selectedAccessToExit && (
        <div
          ref={selectedContentRef}
          className="w-100"
          style={{
            border: "1px #dfdfdf solid",
            borderRadius: 4,
            boxShadow:
              "rgba(0, 0, 0, 0.04) 0px 1px 3px 0px,rgba(0, 0, 0, 0.06) 0px 1px 2px 0px",
            scrollMarginTop: 80,
          }}
        >
          <div className="w-100 p-4 border-box flex">
            {photos && photos.length > 0 && selectedPhoto && (
              <div style={{ width: 125, height: 125 }}>
                <div
                  className="flex align-items-center"
                  style={{
                    minWidth: 125,
                    width: 125,
                    height: 125,
                    backgroundColor: "black",
                    borderRadius: 4,
                    marginRight: 8,
                    overflow: "hidden",
                    cursor: "pointer",
                  }}
                  onClick={() => setShowPictureModal(true)}
                >
                  <img
                    // src={selectedPhoto?.url}
                    src={imageToShow}
                    alt=""
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                </div>
              </div>
            )}
            {photos && photos.length > 0 && (
              <div
                style={{ height: 125 }}
                className="border-box pl-4 pr-4 flex flex-dir-col align-items-center justify-content-start gap-2"
              >
                {photos.map((item, index) => (
                  <div
                    key={`${item?.type}-${index}`}
                    className={
                      selectedPhoto?.type === item?.type
                        ? "photo-button-preview-selected"
                        : "photo-button-preview"
                    }
                    style={{ cursor: "pointer" }}
                    onClick={() => setSelectedPhoto(item)}
                  >
                    <IconifyIcon
                      icon={
                        item?.type === "photo id"
                          ? "fa-regular:id-card"
                          : item?.type === "plates"
                            ? "fa-solid:car-alt"
                            : item?.type === "visitor"
                              ? "fa-solid:user"
                              : "fa-solid:laptop"
                      }
                    />
                  </div>
                ))}
              </div>
            )}
            {photos && photos.length > 0 && selectedPhoto && (
              <Divider orientation="vertical" flexItem />
            )}
            <div
              style={{
                height:
                  photos && photos.length > 0 && selectedPhoto
                    ? "auto"
                    : "auto",
                marginLeft:
                  photos && photos.length > 0 && selectedPhoto ? 12 : 0,
              }}
              className="w-100 flex flex-dir-col justify-content-start"
            >
              <div className="w-100 flex align-item-center justify-content-between">
                <p className="font-16 font-w-700 subtitle-color">
                  {selectedAccessToExit?.visitor}
                </p>
                <div style={{ minWidth: 20 }}>
                  <IconifyIcon
                    icon={"material-symbols:close"}
                    className="subtitle-color pointer"
                    style={{ fontSize: 22 }}
                    onClick={() => setSelectedAccessToExit(null)}
                  />
                </div>
              </div>
              <div style={{ width: "100%", paddingTop: 8 }}>
                <p className="font-12 subtitle-color">
                  {labels.visited}: {selectedAccessToExit?.user}
                </p>
                <p className="font-12 subtitle-color mt-1">
                  {labels.unit}: {selectedAccessToExit?.unit}
                </p>
                <p className="font-12 subtitle-color mt-1">
                  {labels.access_point}:{" "}
                  {
                    selectedAccessToExit?.entrance_transaction?.gate
                      ?.access_point?.name
                  }
                </p>
              </div>
            </div>
          </div>
          <Divider />
          <div
            className="flex aling-items-center justify-content-between p-4"
            style={{ alignItems: "center" }}
          >
            <div>
              <p className="font-12 subtitle-color">{labels.entrance}:</p>
              <p className="font-14 subtitle-color">
                <span className="font-w-700">
                  {dayjs(
                    selectedAccessToExit?.entrance_transaction?.datetime,
                  ).format("DD/MMM hh:mm")}
                </span>
              </p>
            </div>
            <Button
              size="small"
              variant="contained"
              disableElevation
              onClick={onLogExit}
            >
              {labels.log_exit}
            </Button>
          </div>
        </div>
      )}
      {accessesToExit && accessesToExit.length > 0 && (
        <div className="w-100">
          {accessesLoading ? (
            <div
              className="w-100 flex align-items-center justify-content-center"
              style={{ height: 100 }}
            >
              <CircularProgress />
            </div>
          ) : (
            <div className="mt-6">
              <p className="body-text-md mb-6">{labels.entries_without_exit}</p>
              {accessesToExit.map((item, index) => (
                <div
                  key={`${item?.id}-${index}`}
                  className={`w-100 flex justify-content-between p-4 border-box pointer ${
                    accessesToExit.length - 1 === index ? "mt-4" : "mt-4"
                  }`}
                  style={{
                    border: "1px #dfdfdf solid",
                    borderRadius: 4,
                  }}
                  onClick={() => setSelectedAccessToExit(item)}
                >
                  <p className="font-14 font-w-700 subtitle-color">
                    {item?.visitor}
                  </p>
                  <p className="font-14 subtitle-color">
                    {labels.entrance}:{" "}
                    <span className="font-w-700">
                      {dayjs(item?.entrance_transaction?.datetime).format(
                        "DD/MMM hh:mm",
                      )}
                    </span>
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ManualExitLog;
